import { forwardRef, useMemo } from "react";
import { Chart } from "ui";
import type { ChartProps } from "ui";
import { ChartCard } from "../../ChartCard";
import type { ErrorResponse } from "../../../../../hooks/useMoneyHashQuery";
import { useChartsFormat } from "./ChartsProvider";
import type { ValueFormat } from "./valueFormat";

type BarChartProps = ChartProps<"bar">;

type BarChartCardProps = {
  title: string;
  subTitle?: string;
  description?: React.ReactNode;
  labels: BarChartProps["labels"];
  datasets: BarChartProps["datasets"];
  /** How the y-axis values and tooltip values are formatted. */
  format?: ValueFormat;
  /** Number of legend series visible by default; the rest start hidden. */
  initialVisibleDatasets?: number;
  /** Show the legend even when there is only a single dataset. */
  showSingleDatasetLegend?: boolean;
  className?: string;
  contentClassName?: string;
  headerAction?: React.ReactNode;
  queryStatus?: "loading" | "error" | "success";
  error?: ErrorResponse | null;
  retry?: () => void;
};

export const BarChartCard = forwardRef<HTMLDivElement, BarChartCardProps>(
  function BarChartCard(
    {
      title,
      subTitle,
      description,
      labels,
      datasets,
      format = "number",
      initialVisibleDatasets = 15,
      showSingleDatasetLegend = false,
      className,
      contentClassName = "pr-0 pl-4 min-h-80 h-auto",
      headerAction,
      queryStatus,
      error,
      retry,
    },
    ref
  ) {
    const { formatXLabel, getValueFormatters } = useChartsFormat();

    const { formatYLabel, formatValue } = useMemo(
      () => getValueFormatters(format),
      [getValueFormatters, format]
    );

    return (
      <ChartCard
        ref={ref}
        title={title}
        subTitle={subTitle}
        description={description}
        className={className}
        contentClassName={contentClassName}
        headerAction={headerAction}
        queryStatus={queryStatus}
        error={error}
        retry={retry}
      >
        <Chart
          type="bar"
          labels={labels}
          datasets={datasets}
          className="h-auto"
        >
          <Chart.Canvas
            className="h-72"
            formatYLabel={formatYLabel}
            formatXLabel={formatXLabel}
            maxYValue={format === "percentage" ? 100 : undefined}
          />
          <Chart.Tooltip
            valueFormatter={formatValue}
            labelFormatter={formatXLabel}
            showTotal={format !== "percentage"}
          />
          <Chart.Legend
            variant="row"
            initialVisibleDatasets={initialVisibleDatasets}
            showSingleDataset={showSingleDatasetLegend}
          />
        </Chart>
      </ChartCard>
    );
  }
);
